import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Tabs } from '@mantine/core';

import { useAuth } from '@/contexts/AuthContext';
import { ChatPanel } from './ChatPanel';

const OPEN_CHAT_PARAM = 'openChat';

type AsideTab = 'chat' | 'partners';

interface EntityChatAsideProps {
  /** Page-local entity context. The chat service resolves the
   * canonical thread anchor server-side. */
  entityType: 'enquiry' | 'report' | 'report_request';
  entityId: string;
  /** Optional second conversation source (admin layout only) — the report
   * page slots its Network Partner cards + per-NP threads here so every
   * conversation lives under one Chat surface. */
  partnersSection?: ReactNode;
  /** The tab to land on: `partners` opens the Partners tab once, as soon as
   * the section is there (a deep link to Armadillo's conversation). */
  initialTab?: AsideTab;
  /** Lands on `initialTab` again whenever this changes: a deep link that
   * names a new target while the analyst has moved on to the other tab. */
  landingKey?: string | null;
}

/** Switch to the Partners tab once per landing key, the first time the
 * section is there while `initialTab` asks for it: the section arrives
 * with the report's assignments, after the aside has mounted, and a later
 * visit to the Chat tab must not be undone. */
function usePartnersLanding(
  initialTab: AsideTab,
  landingKey: string | null,
  hasPartners: boolean,
  setActiveTab: (tab: AsideTab) => void
) {
  // The key landed on; '' for a landing without one.
  const landedRef = useRef<string | null>(null);
  useEffect(() => {
    const key = landingKey ?? '';
    if (landedRef.current === key || initialTab !== 'partners' || !hasPartners) {
      return;
    }
    landedRef.current = key;
    setActiveTab('partners');
  }, [initialTab, landingKey, hasPartners, setActiveTab]);
}

/**
 * Aside-embedded chat — Phase 09 layout overhaul.
 *
 * Renders chat content directly (no Affix, no Drawer). Wrapped by the
 * persistent ``PageAside`` provided by the global Layout.
 *
 * Admins get ONE merged conversation: internal notes interleave with
 * the client thread (yellow bubbles + "Internal" badge) and the
 * composer's "Internal" checkbox sends a note instead of a client
 * reply — no separate Internal tab. A Partners tab appears when the
 * caller supplies one. Clients see the single client panel (private
 * notes are server-filtered from their listing).
 *
 * ``?openChat=client|internal`` deep-links (pre-merge vintage) both
 * land on the merged chat.
 */
export function EntityChatAside({
  entityType,
  entityId,
  partnersSection,
  initialTab = 'chat',
  landingKey = null,
}: Readonly<EntityChatAsideProps>) {
  const auth = useAuth();
  const user = auth?.user;
  const isAdmin = user?.isAdministrator === true;
  const isClient = user?.isClient === true;

  const [searchParams, setSearchParams] = useSearchParams();
  const hasOpenChatParam = searchParams.get(OPEN_CHAT_PARAM) !== null;
  const [activeTab, setActiveTab] = useState<AsideTab>('chat');
  const consumedRef = useRef(false);
  usePartnersLanding(initialTab, landingKey, Boolean(partnersSection), setActiveTab);

  useEffect(() => {
    if (!hasOpenChatParam || consumedRef.current) {
      return;
    }
    if (!user || (!isAdmin && !isClient)) {
      return;
    }
    consumedRef.current = true;
    // Every historical openChat value (client/internal) now means the
    // merged chat tab; consume the param either way.
    setActiveTab('chat');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(OPEN_CHAT_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [hasOpenChatParam, user, isAdmin, isClient, setSearchParams]);

  if (!user || (!isAdmin && !isClient)) {
    return null;
  }

  if (!isAdmin) {
    // Client sends relay files via the SES on-behalf-of email; the
    // backend enforces its (lower) size cap at send time.
    return (
      <ChatPanel
        entityType={entityType}
        entityId={entityId}
        kind="client_thread"
        canSendPrivate={false}
        enableAttachments
      />
    );
  }

  const mergedPanel = (
    <ChatPanel
      entityType={entityType}
      entityId={entityId}
      kind="client_thread"
      canSendPrivate
      mergeInternal
      enableAttachments
    />
  );

  if (!partnersSection) {
    return mergedPanel;
  }

  // A stale Partners selection (e.g. after navigating to a report with no
  // dispatched NPs) falls back to the merged chat above; here the section
  // exists, so both tabs are live. Only the visible one is mounted — a
  // hidden panel would still poll its thread.
  //
  // These two sit inside the aside's own "Chat" tab, so they are drawn as
  // pills and named for what they hold ("Client chat" / "NP chat", as the
  // case page calls them) rather than as a second row of tabs repeating
  // "Chat".
  return (
    <Tabs
      value={activeTab}
      onChange={(v) => {
        if (v === 'chat' || v === 'partners') {
          setActiveTab(v);
        }
      }}
      keepMounted={false}
      variant="pills"
      radius="xl"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Tabs.List>
        <Tabs.Tab value="chat">Client chat</Tabs.Tab>
        <Tabs.Tab value="partners">NP chat</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel
        value="chat"
        pt="md"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {mergedPanel}
      </Tabs.Panel>
      <Tabs.Panel
        value="partners"
        pt="md"
        style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {partnersSection}
      </Tabs.Panel>
    </Tabs>
  );
}
